// The "What can I do?" panel behind the header's ⓘ button. This content is the
// app's user-facing promise of what chat can do, so it is role-split and lists
// ONLY capabilities backed by a v1-chat tool for that role (OPERATOR_TOOLS /
// CUSTOMER_TOOLS in supabase/functions/v1-chat/index.ts). When a chat tool is
// added or removed, update the matching section in the same change.
//
// A capability the operator has over MCP but not in chat is a PARITY BUG, not a
// reason to leave a line out of this panel — the fix is to add the chat tool.
// CLAUDE.md § Capability parity.

interface InfoSection {
  title: string
  items: string[]
}

export const OPERATOR_INFO_SECTIONS: InfoSection[] = [
  {
    title: 'Bookings & schedule',
    items: [
      // Shown on TWO surfaces: the assistant panel (day/week answers arrive as
      // a summary card that opens that day on the Calendar screen) and the
      // tenant-slug full-page chat (which still draws the calendar inline) —
      // so this line stays neutral about the form the answer takes.
      "Ask what's on any day or week — today, tomorrow, or any date",
      'List one customer\'s appointments, all future bookings, or a filtered set',
      'Book a customer in',
      // Backed by create_booking with service_ids. Worded as "one visit" rather
      // than "several bookings" because that is what the operator is arranging —
      // though each treatment really is its own appointment, which is what makes
      // it payable and checkable-in separately.
      'Book several treatments as one back-to-back visit — say them in the order they should run (only while the setting below is on: since 2026-09-15 it governs you too)',
      'Reschedule, cancel, complete, or mark a no-show — with a reason, if you want one recorded',
      // Backed by check_in_booking. Worded to keep arrival and completion apart:
      // they were one action until 2026-08-17 and conflating them again is how a
      // session gets closed off before it has happened.
      'Check someone in when they arrive — the right package credit is deducted, and you are told if none applied',
      'Check them out when the session is finished, recording the actual finishing time',
      'Find open slots for a service',
      'Check which reminders went out for a booking',
    ],
  },
  {
    title: 'Customers',
    items: [
      'Type @ to pick a customer from a list instead of spelling their name out',
      'Find anyone by name, email, or phone — a first name alone works, misspellings are tolerated: recent visits and chats match first, and you confirm with a tap when several people share it',
      'Full profile: notes, upcoming visits, package balances',
      // Backed by list_segment_fields + preview_segment. Phrased as a COUNT
      // because that is what those tools return — never a list of people.
      'Count how many customers fit a pattern — regulars who have lapsed, big spenders, repeat no-shows, anyone who never rebooked',
      'Add a customer and invite them to this app',
      'Correct a name, email, or phone number',
      'Update goals, health notes, date of birth, and marketing consent',
      'Archive someone who has left, or bring them back — nothing is ever deleted, so their history stays intact',
      'Record, correct, or remove a measurement',
      'View progress records',
    ],
  },
  {
    title: 'Notes & voice notes',
    items: [
      'Record a conversation and get it written up as a note',
      'Pick the customer the chat is about — every request refers to them, and notes and photos file to their record automatically',
      'Choose a note style to control how notes are summarised',
      'Save customer notes with AI-suggested tags (you confirm first)',
      'Fix the wording or tags on a note already saved',
      'Attached photos and PDFs are saved to the customer\'s record and linked to the note',
      'Delete a note or one of its files — the stored file is removed too (you confirm first)',
      'Find customers by what their notes say — by keyword or tag',
      'Search one customer\'s notes by topic — matches meaning too ("knee problems" finds "patella discomfort")',
      'Create, edit, or delete note styles',
    ],
  },
  {
    title: 'Services & resources',
    items: [
      'Name a treatment however you say it — "lymphatic drainage", "the 45 min one"',
      'List, create, or update services — price, duration, category, tax, order',
      // Backed by the sort/order parameters on list_services and list_packages.
      // Worth its own line: it is what an operator would otherwise assume chat
      // cannot answer, and the panel is the promise of what chat can do.
      'Ask which is your dearest, longest or newest treatment — or your biggest package',
      'Hide a service from customers while you can still book it yourself',
      'Set how far ahead customers must book anything — you can always book a walk-in yourself',
      'Require one treatment to be booked further ahead than the rest',
      'Give one treatment its own intake questionnaire — otherwise the default template applies',
      // Backed by set_multi_service_booking. Named as a whole-business switch:
      // since 2026-09-15 it gates the operator's own surfaces too.
      'Turn on or off whether several treatments can be booked as one visit — for clients and for you',
      'See the category headings your menu uses, and rename or merge one across every service',
      'List, add, or update rooms, machines, and staff',
      'Retire a service or resource, or bring a retired one back — nothing is ever deleted',
    ],
  },
  {
    title: 'Availability',
    items: [
      'View or change weekly business hours',
      "View or change a resource's weekly schedule, or see every resource's at once",
      'Block out a one-off closure — the whole centre, or just one room or machine',
      'Adjust a closure, or lift it to reopen the time',
      // Deliberately worded against the closure lines above: the difference is
      // the whole reason calendar notes exist as a separate thing.
      'Pin a memo to a day on the calendar — a reminder for whoever is working, which leaves the time bookable',
      'Read, edit, or remove calendar memos',
    ],
  },
  {
    title: 'Money, packages & reports',
    items: [
      'Business reports: revenue, busiest times, top customers',
      'Payments received and refunds issued',
      "A booking's ledger and balance due",
      'Find a package by the words you use for it — "the 14 day body reset" — or by what it is for: "a program to lose weight"',
      'Packages on offer, sales, and remaining credits',
      'Sell a package — the credits are granted straight away, and the money is recorded with it; a sale made on an earlier day can carry that date, and its expiry runs from it',
      'Sell treatments on their own, ahead of time — "3 lymphatic and a VacuFit" — as sessions that never expire, used one per booking, at a discounted price per treatment when you agree one',
      'Create, edit, or retire a package on offer — credits already sold keep working',
      'Give each service in a package its own session limit (3× lymphatic + 2× HIFU), tracked separately per customer',
      'Or share one pool of credits across a whole category — “10 credits for Lymphatic Drainage”, spendable on any treatment in it',
      'Set how many credits a treatment uses up, so a longer one costs 2 and a shorter one 1 out of the same package',
      'Correct a sale — extend an expiry, or fix the sessions used',
      'Void a sale recorded against the wrong person (you confirm first)',
      'Correct a price on a booking, or link and unlink a package',
    ],
  },
  {
    title: 'Taking payment',
    items: [
      'Work out how a session can be paid for — which package credits apply, and what is still owed',
      'Settle a session from a package credit, naming which credit to spend',
      'Send a card payment link for what is outstanding — on a session or a package sale — by email, text and their app',
      'Sell a new package by card link — their credits appear as soon as the payment lands',
      'Choose which of the two this business reaches for first',
      'Record money already taken elsewhere — for a session or a package sale — on the payment gateway, in cash, or by bank transfer, dated the day it was taken if that was earlier (up to 2 months back)',
      'Give money back — the only way to correct a payment, since the ledger itself is never edited',
    ],
  },
  {
    title: 'Invoices, receipts & memberships',
    items: [
      'Every payment emails its own receipt automatically, and every refund its adjustment note — nothing to remember to send',
      'Email a customer their document on demand — for an appointment or a package sale; a receipt if it is paid, a tax invoice if not',
      'Service receipts for health fund / insurance claims — one per session once it is delivered and paid, naming who gave it and their provider numbers — or only the one for their own fund, once you say which fund the client is with; record who delivered a session and re-send a lost one',
      'Re-send any document exactly as it was issued — a lost email never means a new number',
      'Set up a recurring membership — they confirm by card once, then it renews, tops up their credits, and receipts every charge',
      'See or stop a membership, and chase one whose card failed',
      'See every invoice, receipt and adjustment note issued, and which ones failed to send',
      'Set the ABN, practice address and GST details that appear on them',
    ],
  },
  {
    title: 'Marketing sequences',
    items: [
      'Build a follow-up campaign: a chain of emails or texts on a schedule',
      'Set when it starts — straight away, the next Monday, or a fixed date',
      'Add and edit steps, with a wait and a send time for each',
      'Activate or pause a campaign — nothing sends until it is active',
      'Enrol a customer, or pause or cancel just their enrolment',
      'See every step sent, and why any were skipped',
      'Delete an unused draft campaign',
    ],
  },
  {
    // Only what a v1-chat tool actually backs. Sending AND SCHEDULING are both
    // absent on purpose: chat can write, preview and test a campaign, but a
    // person presses both buttons on the Campaigns screen — scheduling is just
    // sending later. Do not add either here without adding the tool.
    title: 'One-off email campaigns',
    items: [
      'Start a campaign as a draft — blank, or copying a previous email',
      'Write the email — headings, text, buttons — and rewrite it whenever you like',
      'Work out who to send it to, and see how many customers that reaches',
      'Save that group as a reusable audience, or adjust one you already saved',
      'Tidy up: archive an audience, or remove a draft campaign you do not need',
      'Send yourself a test copy to read before anything goes out',
      'Check why a campaign is not ready to send yet',
      'After a send: how it performed — opens, clicks per link, unsubscribes, and who was skipped and why',
      'A customer who unsubscribes has their marketing consent switched off automatically',
      'Sending and scheduling are done by you, on the Campaigns screen — I will do neither',
    ],
  },
  {
    title: 'Treatment cards',
    items: [
      'Define what you record about a session — settings, areas worked, how it went',
      'Ask what a client had last time, and record today carried forward from it',
      'Correct a card you filled in earlier',
      'Cards are never shown to clients, and they save into the client’s notes',
    ],
  },
  {
    title: 'Intake forms',
    items: [
      'Build the questionnaire new clients answer — questions, yes/no checks, consent ticks',
      'Write the invitation email each form is sent with — every form has its own, because what you ask for differs',
      'Add a short text message to a form, or leave it out and the standard line goes',
      'Mark a yes/no question as needing medical clearance when answered yes',
      "Clients are sent the form automatically the first time they book a service they haven't done before",
      'Give one treatment its own form, or let every service use the default template',
      'Send or re-send a client their form link by email and text — asking again re-sends the same link',
      'See who has answered, and read back what a client answered',
      'Fill one in for a client who is with you',
      'Record medical clearance once you have reviewed a flagged answer',
    ],
  },
  {
    title: 'Business admin',
    items: [
      'Reminder and notification delivery logs',
      'Customer conversations, and reopening one that was resolved too soon',
      'Message a customer by text, WhatsApp, email or in-app — start a conversation or answer one',
      'Who can sign in to manage the business — invite someone, or revoke their access',
      'Add a staff member, or change what someone on the team can do',
      'Business details, booking link, and who reminders and invoices come from',
      'Reminder wording: write, edit, or switch which one is live on each channel',
      'Scheduled digests and alerts — create, edit, pause, or remove one',
    ],
  },
  {
    title: 'Attachments & rich answers',
    items: [
      'Attach up to 10 photos, PDFs, or text files to analyse or write up',
      'Replies can be calendars, booking lists, charts, tables, or booking cards',
    ],
  },
]

export const CUSTOMER_INFO_SECTIONS: InfoSection[] = [
  {
    title: 'Book an appointment',
    items: [
      'Browse services and prices',
      'Ask for a treatment by name, even a partial or shortened one',
      'Check available times',
      'Book — the details are confirmed with you first',
    ],
  },
  {
    title: 'Your appointments',
    items: [
      'See your upcoming and past appointments',
      'Reschedule or cancel a booking',
    ],
  },
  {
    title: 'Packages',
    items: ['See the session packages on offer'],
  },
  {
    title: 'Progress',
    items: ['Log your progress with a photo or note — tap 📸 Log progress'],
  },
  {
    title: 'Talk to a person',
    items: ['Ask for a team member any time — they take over right here in this chat'],
  },
]

export const STAFF_ACCESS_NOTE =
  'Some of these may be limited by your access level — anything blocked will say so when you ask.'

interface InfoPanelProps {
  visible: boolean
  // Which capability list to show. Operator = owner/staff on a management
  // surface; everything else (including a customer's own thread) is customer.
  audience: 'operator' | 'customer'
  // True when the signed-in operator is staff rather than owner — appends a
  // note that role permissions may block some listed actions (money and
  // business settings default to owner-only).
  staffNote?: boolean
  onClose: () => void
}

export function InfoPanel({ visible, audience, staffNote, onClose }: InfoPanelProps) {
  if (!visible) return null

  const sections = audience === 'operator' ? OPERATOR_INFO_SECTIONS : CUSTOMER_INFO_SECTIONS

  return (
    // pointer-events-none here + pointer-events-auto on the children mirrors RN's
    // `pointerEvents: 'box-none'`: the overlay itself is inert, its children are not.
    <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center">
      <button
        type="button"
        className="pointer-events-auto absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        aria-label="Close panel"
      />
      <div className="pointer-events-auto relative flex max-h-[80%] w-[90%] max-w-[400px] flex-col overflow-hidden rounded-xl bg-card shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
        <div className="flex items-center justify-between border-b border-border px-3.5 py-3">
          <h2 className="text-[13px] font-bold text-foreground">What can I do?</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-0.5 text-[13px] text-muted-foreground/70"
          >
            ✕
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-3">
          {sections.map(section => (
            <div key={section.title} className="border-b border-border/50 py-2.5">
              <h3 className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.5px] text-muted-foreground">
                {section.title}
              </h3>
              {section.items.map(item => (
                <div key={item} className="mb-[3px] flex gap-1.5">
                  <span className="text-[13px] leading-[19px] text-muted-foreground/70">·</span>
                  <span className="min-w-0 flex-1 text-[13px] leading-[19px] text-foreground">{item}</span>
                </div>
              ))}
            </div>
          ))}
          {audience === 'operator' && staffNote && (
            <p className="pt-2.5 text-[12px] leading-[17px] text-muted-foreground">{STAFF_ACCESS_NOTE}</p>
          )}
        </div>
      </div>
    </div>
  )
}
